// PreventiApp · Sprint 3 · Etapa 7 — Lista de observaciones según RLS.
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { listObservaciones, listProfileNames } from '../../data/supabaseAdmin';
import { DOMAINS, CONDUCTS, REGISTRO_ESTADOS } from '../../../src/data/observationEngine';

const ESTADOS_FILTER = ['', 'registrado', 'editado', 'en_revision', 'en_seguimiento', 'cerrado', 'marcado_erroneo'];
const MODOS = ['', 'operativo', 'induccion'];

const conductaLabel = (code) => {
  if (!code) return '—';
  const dom = code.split('-')[0];
  return (CONDUCTS[dom] || []).find((c) => c.code === code)?.label || code;
};
const fmt = (iso) => new Date(iso).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export default function AdminObservaciones() {
  const [items, setItems] = useState([]);
  const [profMap, setProfMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fEstado, setFEstado] = useState('');
  const [fModo, setFModo] = useState('');
  const [fDominio, setFDominio] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError('');
      try {
        const rows = await listObservaciones({
          estado: fEstado || undefined, modo: fModo || undefined, dominio_code: fDominio || undefined,
        });
        if (cancelled) return;
        setItems(rows);
        const authors = await listProfileNames(rows.map((r) => r.autor_id));
        if (!cancelled) setProfMap(authors);
      } catch (e) { if (!cancelled) setError(e.message); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [fEstado, fModo, fDominio]);

  const counts = useMemo(() => {
    const m = {};
    items.forEach((i) => { m[i.estado] = (m[i.estado] || 0) + 1; });
    return m;
  }, [items]);

  return (
    <div className="p-8 max-w-6xl" data-testid="page-observaciones">
      <div className="mb-5">
        <h1 className="text-2xl font-extrabold text-[#0B1B5E]">Observaciones</h1>
        <p className="text-xs text-[#475472] mt-1">Se muestran automáticamente las observaciones que tu rol tiene permitido ver.</p>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <select data-testid="obs-filter-estado" value={fEstado} onChange={(e) => setFEstado(e.target.value)} className="px-3 py-2 rounded-xl border border-[#DDE6F7] text-sm">
          {ESTADOS_FILTER.map((s) => <option key={s} value={s}>{s === '' ? 'Todos los estados' : (REGISTRO_ESTADOS[s]?.label || s)}</option>)}
        </select>
        <select data-testid="obs-filter-modo" value={fModo} onChange={(e) => setFModo(e.target.value)} className="px-3 py-2 rounded-xl border border-[#DDE6F7] text-sm">
          {MODOS.map((m) => <option key={m} value={m}>{m === '' ? 'Todos los modos' : m}</option>)}
        </select>
        <select data-testid="obs-filter-dominio" value={fDominio} onChange={(e) => setFDominio(e.target.value)} className="px-3 py-2 rounded-xl border border-[#DDE6F7] text-sm">
          <option value="">Todos los dominios</option>
          {DOMAINS.map((d) => <option key={d.id} value={d.id}>{d.id}: {d.name}</option>)}
        </select>
        <div className="ml-auto flex gap-2 text-xs">
          {Object.entries(counts).map(([k, v]) => (
            <span key={k} className="px-2 py-1 rounded-full bg-[#F4F7FE] text-[#0B1B5E] font-bold" data-testid={`obs-count-${k}`}>{k}: {v}</span>
          ))}
        </div>
      </div>

      {error && <div className="mb-4 p-3 rounded-xl bg-[#FEE2E2] text-[#991B1B] text-sm">{error}</div>}

      <div className="bg-white border border-[#E5EAF5] rounded-2xl overflow-hidden">
        <table className="w-full text-sm" data-testid="obs-table">
          <thead className="bg-[#F4F7FE] text-[#0B1B5E] text-xs">
            <tr>
              <th className="px-4 py-3 text-left font-bold">Fecha</th>
              <th className="px-4 py-3 text-left font-bold">Dominio</th>
              <th className="px-4 py-3 text-left font-bold">Conducta</th>
              <th className="px-4 py-3 text-left font-bold">Curso</th>
              <th className="px-4 py-3 text-left font-bold">Autor</th>
              <th className="px-4 py-3 text-left font-bold">Modo</th>
              <th className="px-4 py-3 text-left font-bold">Estado</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {items.map((o) => (
              <tr key={o.id} className="border-t border-[#E5EAF5]" data-testid={`obs-row-${o.id.slice(0,8)}`}>
                <td className="px-4 py-3 text-xs">{fmt(o.creado_en)}</td>
                <td className="px-4 py-3 text-xs font-bold">{o.dominio_code}</td>
                <td className="px-4 py-3 text-xs">{conductaLabel(o.conducta_code)}</td>
                <td className="px-4 py-3 text-xs">{o.curso_texto || '—'}</td>
                <td className="px-4 py-3 text-xs text-[#475472]">{profMap[o.autor_id]?.nombre_completo || '—'}</td>
                <td className="px-4 py-3 text-xs"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${o.modo_registro === 'induccion' ? 'bg-[#FFF1D6] text-[#7A4A0B]' : 'bg-[#EEF0FE] text-[#5346E6]'}`}>{o.modo_registro}</span></td>
                <td className="px-4 py-3 text-xs"><span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#F4F7FE] text-[#0B1B5E]">{REGISTRO_ESTADOS[o.estado]?.label || o.estado}</span></td>
                <td className="px-4 py-3 text-right">
                  <Link to={`/admin/observaciones/${o.id}`} data-testid={`obs-link-${o.id.slice(0,8)}`} className="text-xs font-bold text-[#5346E6] hover:underline">Detalle</Link>
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-[#475472]">Sin observaciones visibles para tu rol.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
